import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";

import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import OpacityOutlinedIcon from "@mui/icons-material/OpacityOutlined"; // Water
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import PropaneOutlinedIcon from "@mui/icons-material/PropaneOutlined"; // Gas
import TungstenOutlinedIcon from "@mui/icons-material/TungstenOutlined"; // Energy
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined"; // Condo
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined"; // IPTU
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";

import { globalMaskDocument, maskPhone } from "../../../../shared/utils";

import {
    InfoRow,
    StatusChip,
    prepareCapitalizationData,
} from "./ModalCapitalizationDetails.helpers";

import { CapitalizationService } from "../../../../shared/services/api/capitalization/CapitalizationService";
import { CapitalizationData } from "../../../../shared/services/api/capitalization/types";
import { useEffect, useState } from "react";
import { globalTranslateMaritalStatusCapitalization } from "../../../../shared/utils";

interface ModalCapitalizationDetailsProps {
    open: boolean;
    onClose: () => void;
    capitalizationId?: string;
}

export default function ModalCapitalizationDetails({
    open,
    onClose,
    capitalizationId,
}: ModalCapitalizationDetailsProps) {
    const theme = useTheme();
    const [data, setData] = useState<CapitalizationData | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (open && capitalizationId) {
            setIsLoading(true);
            CapitalizationService.getById(capitalizationId)
                .then((result) => {
                    if (result instanceof Error) {
                        console.error(result.message);
                    } else {
                        setData(result);
                    }
                })
                .finally(() => setIsLoading(false));
        } else {
            setData(null);
        }
    }, [open, capitalizationId]);

    if (!open) return null;

    /* ---------------- LOADING ---------------- */
    if (isLoading || !data)
        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
            >
                <DialogContent
                    sx={{
                        py: 6,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <CircularProgress size={28} />
                    <Typography sx={{ ml: 2 }}>Carregando detalhes...</Typography>
                </DialogContent>
            </Dialog>
        );

    /* ---------------- DADOS PRÉ-PROCESSADOS ---------------- */
    const {
        fmt,
        fdate,
        realEstateName,
        insurerName,
        titleAmount,
        plan,
        startDate,
        endDate,
        addressLine,
    } = prepareCapitalizationData(data);

    return (
        <>
            {/* ===================== MODAL PRINCIPAL ===================== */}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
            >
                {/* HEADER */}
                <DialogTitle
                    sx={{
                        px: { xs: 3, md: 4 },
                        py: { xs: 3, md: 3.5 },
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: theme.palette.primary.contrastText,
                        position: "relative",
                    }}
                >
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Typography variant="h5" fontWeight={500}>
                                Detalhes do Título
                            </Typography>
                            <StatusChip status={data?.status} />
                        </Stack>
                    </Stack>

                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 16,
                            top: 16,
                            color: "inherit",
                            bgcolor: "rgba(255,255,255,0.16)",
                            "&:hover": { bgcolor: "rgba(255,255,255,0.24)" },
                        }}
                        size="small"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>

                {/* BODY */}
                <DialogContent
                    dividers
                    sx={{
                        px: { xs: 3, md: 4 },
                        py: 2,
                        bgcolor: "background.default",
                    }}
                >
                    <Stack spacing={2}>
                        {/* CARD 1 — Dados Gerais */}
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Dados do Título
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <InfoRow
                                            icon={<BusinessOutlinedIcon fontSize="small" />}
                                            label="Imobiliária"
                                            value={realEstateName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <InfoRow
                                            icon={<BusinessOutlinedIcon fontSize="small" />}
                                            label="Seguradora"
                                            value={insurerName}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={3} sx={{ mt: 0.5 }}>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoRow
                                            icon={<EventAvailableOutlinedIcon fontSize="small" />}
                                            label="Início vigência"
                                            value={fdate(startDate)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoRow
                                            icon={<EventAvailableOutlinedIcon fontSize="small" />}
                                            label="Fim vigência"
                                            value={fdate(endDate)}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6} md={4}>
                                        <InfoRow
                                            icon={<DescriptionOutlinedIcon fontSize="small" />}
                                            label="Plano"
                                            value={plan}
                                        />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>

                        {/* CARD 2 — Inquilino / Proprietário */}
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                                    <Stack flex={1} spacing={1.5}>
                                        <Typography variant="subtitle2">Inquilino</Typography>

                                        <InfoRow
                                            icon={<PersonOutlineOutlinedIcon fontSize="small" />}
                                            label="Nome"
                                            value={data?.tenantName}
                                        />
                                        <InfoRow
                                            icon={<DescriptionOutlinedIcon fontSize="small" />}
                                            label="Documento"
                                            value={globalMaskDocument(data?.tenantDocument)}
                                        />
                                        <InfoRow
                                            icon={<EmailOutlinedIcon fontSize="small" />}
                                            label="E-mail"
                                            value={data?.tenantEmail}
                                        />
                                        <InfoRow
                                            icon={<PhoneOutlinedIcon fontSize="small" />}
                                            label="Telefone"
                                            value={maskPhone(data?.tenantPhone)}
                                        />
                                        <InfoRow
                                            icon={<PublicOutlinedIcon fontSize="small" />}
                                            label="Nacionalidade"
                                            value={data?.tenantNationality}
                                        />
                                        <InfoRow
                                            icon={<FavoriteBorderOutlinedIcon fontSize="small" />}
                                            label="Estado Civil"
                                            value={globalTranslateMaritalStatusCapitalization(data?.tenantMaritalStatus)}
                                        />
                                        <InfoRow
                                            icon={<WorkOutlineOutlinedIcon fontSize="small" />}
                                            label="Profissão"
                                            value={data?.tenantProfession}
                                        />
                                        <InfoRow
                                            icon={<AttachMoneyOutlinedIcon fontSize="small" />}
                                            label="Renda Mensal"
                                            value={fmt(data?.tenantMonthlyIncome)}
                                        />
                                        <InfoRow
                                            icon={<CakeOutlinedIcon fontSize="small" />}
                                            label="Data de Nascimento"
                                            value={fdate(data?.tenantBirthDate)}
                                        />
                                        <InfoRow
                                            icon={<BadgeOutlinedIcon fontSize="small" />}
                                            label="RG / Órgão / UF / Emissão"
                                            value={[
                                                data?.tenantIdentity,
                                                data?.tenantIssuer,
                                                data?.tenantState,
                                                data?.tenantIssueDate ? fdate(data.tenantIssueDate) : null
                                            ].filter(Boolean).join(" - ")}
                                        />
                                    </Stack>

                                    <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />

                                    <Stack flex={1} spacing={1.5}>
                                        <Typography variant="subtitle2">Proprietário</Typography>

                                        <InfoRow
                                            icon={<PersonOutlineOutlinedIcon fontSize="small" />}
                                            label="Nome"
                                            value={data?.ownerName}
                                        />
                                        <InfoRow
                                            icon={<DescriptionOutlinedIcon fontSize="small" />}
                                            label="Documento"
                                            value={globalMaskDocument(data?.ownerDocument)}
                                        />
                                        <InfoRow
                                            icon={<EmailOutlinedIcon fontSize="small" />}
                                            label="E-mail"
                                            value={data?.ownerEmail}
                                        />
                                        <InfoRow
                                            icon={<PhoneOutlinedIcon fontSize="small" />}
                                            label="Telefone"
                                            value={maskPhone(data?.ownerPhone)}
                                        />
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* CARD 3 — Endereço */}
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Endereço do Imóvel
                                </Typography>

                                <InfoRow
                                    icon={<HomeWorkOutlinedIcon fontSize="small" />}
                                    label="Endereço completo"
                                    value={addressLine}
                                />
                            </CardContent>
                        </Card>

                        {/* CARD 4 — Valores */}
                        <Card variant="outlined" sx={{ borderRadius: 3 }}>
                            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Valores
                                </Typography>

                                <Box
                                    sx={{
                                        backgroundColor: theme.palette.grey[50],
                                        borderRadius: 2,
                                        p: 2.5,
                                    }}
                                >
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <InfoRow
                                                icon={<PaidOutlinedIcon fontSize="small" />}
                                                label="Aluguel"
                                                value={fmt(data?.rentAmount)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <InfoRow
                                                icon={<ApartmentOutlinedIcon fontSize="small" />}
                                                label="Condomínio"
                                                value={fmt(data?.condominiumAmount)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <InfoRow
                                                icon={<ReceiptLongOutlinedIcon fontSize="small" />}
                                                label="IPTU"
                                                value={fmt(data?.iptuAmount)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <InfoRow
                                                icon={<OpacityOutlinedIcon fontSize="small" />}
                                                label="Água"
                                                value={fmt(data?.waterAmount)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <InfoRow
                                                icon={<TungstenOutlinedIcon fontSize="small" />}
                                                label="Energia"
                                                value={fmt(data?.energyAmount)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <InfoRow
                                                icon={<PropaneOutlinedIcon fontSize="small" />}
                                                label="Gás"
                                                value={fmt(data?.gasAmount)}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 2 }} />

                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Stack alignItems="flex-end">
                                            <Typography fontWeight={700} variant="h6" color="primary">Valor do Título</Typography>
                                            <Typography fontWeight={600} variant="h5">{fmt(titleAmount)}</Typography>
                                        </Stack>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Stack>
                </DialogContent>

                {/* FOOTER */}
                <DialogActions
                    sx={{
                        px: { xs: 3, md: 4 },
                        py: 2.5,
                        bgcolor: "background.paper",
                        justifyContent: "space-between",
                    }}
                >
                    <Button onClick={onClose}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
